import React from 'react';
import moment from 'moment';
import { CgSpinner } from 'react-icons/cg';
import './bookingsTables.css';
import FreeRoomButton from './freeRoomButton.js';
import UnavailableRoom from './unavailableRoom.js';
import sadSE from '../assets/SE_sad.svg';
import { TooltipList } from '../common/TooltipList';


// props:
// roomId
// lesson
// cancelBookingFunction
// onFreeRoomClick - handler when clicking a free room
//
//
export function DailyBookingsTableCell(props) {
    const isRefreshingSlot = Boolean(
        props.refreshingSlot &&
        props.refreshingSlot.roomId === props.roomId &&
        props.refreshingSlot.lessonId === props.lesson.name &&
        props.refreshingSlot.date === props.date
    );

    if (isRefreshingSlot) {
        const visualClass = props.refreshingSlot?.previousVisual === 'occupied'
            ? 'bookingsSlotRefreshing--occupied'
            : 'bookingsSlotRefreshing--free';
        return (
            <td className='bookingsTableSingleRoomData' key={props.roomId}>
                <div className={`bookingsSlotRefreshing ${visualClass}`}>
                    <CgSpinner className='bookingsSlotRefreshingSpinner' />
                </div>
            </td>
        );
    }

    // Safely retrieve booking data for the cell; fall back to empty values if undefined
    const bookingInfo = props.rooms?.[props.roomId]?.bookingData?.[props.lesson.name] || {
        userId: '',
        class: '',
        ownBooking: false,
        questions: [],
        bookedBy: '',
        origin: '',
        userCogId: '',
        bookedByCogId: ''
    };
    const questionEntries = Array.isArray(bookingInfo.questions) ? bookingInfo.questions : [];
    const hasQuestions = questionEntries.length > 0;
    const proxyBooked = bookingInfo.origin === 'PROXY';
    const isBulk = Boolean(bookingInfo.seriesId);
    const ownBooking = bookingInfo.ownBooking === true;
    const cancellable = bookingInfo.origin !== 'TIMETABLED';

    const roomPerm = props.rooms?.[props.roomId]?.perm || 'view';
    const isPastDate = moment(props.date, 'DDMMyyyy').isBefore(moment(), 'day');
    const readOnlyPast = isPastDate && roomPerm !== 'manage';
    const viewOnly = roomPerm === 'view';
    // Only display question/proxy indicators when the user's permission level allows it.
    const showQuestions = hasQuestions && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const showProxy = proxyBooked && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const showBulk = isBulk && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const canEditQuestions = !readOnlyPast && !viewOnly && hasQuestions &&
        (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));

    const handleEditClick = () => props.onEditClick(
        props.roomId,
        props.lesson.name,
        props.rooms[props.roomId].name,
        bookingInfo
    );

    // Build grouped tooltip content for questions, proxy and bulk bookings
    const tooltipGroups = [];
    if (showQuestions) {
        tooltipGroups.push({ color: '#c48e38', items: questionEntries });
    }
    if (showProxy) {
        tooltipGroups.push({ color: '#54abcd', items: [`Booked by ${bookingInfo.bookedBy}`] });
    }
    if (showBulk) {
        tooltipGroups.push({ color: '#ff99ff', items: ['Bulk booking'] });
    }

    const applyTooltips = (content) => (
        tooltipGroups.length > 0 ? (
            <TooltipList groups={tooltipGroups}>{content}</TooltipList>
        ) : (
            content
        )
    );

    const bookedContent = (
        <React.Fragment>
            {bookingInfo.userId}
            <br />
            {bookingInfo.class}
        </React.Fragment>
    );

    const ownBookingContent = (
        <UnavailableRoom
            user={bookingInfo.userId}
            class={bookingInfo.class}
            lesson={props.lesson.name}
            room={props.roomId}
            roomName={props.rooms[props.roomId].name}
            cancelBookingFunction={props.cancelBookingFunction}
            onEditClick={canEditQuestions ? handleEditClick : undefined}
            onCancelClick={showBulk ? () => props.onBulkCancelClick(
                props.roomId,
                props.lesson.name,
                props.rooms[props.roomId].name,
                bookingInfo,
                props.date
            ) : undefined}
        />
    );

    const freeRoomContent = (
        <FreeRoomButton
            lesson={props.lesson.name}
            room={props.roomId}
            roomName={props.rooms[props.roomId].name}
            onClick={props.onFreeRoomClick}
        />
    );

    if (readOnlyPast) {
        const readOnlyInner = (
            <>
                {bookingInfo.userId ? bookedContent : <div>Free</div>}
                {showQuestions && <div className='questionIndicator'></div>}
                {showProxy && <div className='proxyIndicator'></div>}
                {showBulk && <div className='bulkIndicator'></div>}
            </>
        );

        return (
            <td className='bookingsTableSingleRoomData' key={props.roomId}>
                {applyTooltips(<div className='pastBookingCell'>{readOnlyInner}</div>)}
            </td>
        );
    }

    if (viewOnly) {
        const viewOnlyContent = bookingInfo.userId !== '' ? bookedContent : <div className='freeRoomButton'>Available</div>;
        return (
            <td className='bookingsTableSingleRoomData' key={props.roomId}>
                {applyTooltips(viewOnlyContent)}
            </td>
        );
    }

    let mainContent;
    if (bookingInfo.userId !== '') {
        if (ownBooking && cancellable && (roomPerm === 'edit' || roomPerm === 'manage')) {
            mainContent = ownBookingContent;
        } else if (!ownBooking && cancellable && roomPerm === 'manage') {
            mainContent = (
                <UnavailableRoom
                    user={bookingInfo.userId}
                    class={bookingInfo.class}
                    lesson={props.lesson.name}
                    room={props.roomId}
                    roomName={props.rooms[props.roomId].name}
                    cancelBookingFunction={props.cancelBookingFunction}
                    onEditClick={canEditQuestions ? handleEditClick : undefined}
                    onCancelClick={showBulk ? () => props.onBulkCancelClick(
                        props.roomId,
                        props.lesson.name,
                        props.rooms[props.roomId].name,
                        bookingInfo,
                        props.date
                    ) : undefined}
                />
            );
        } else {
            mainContent = bookedContent;
        }
    } else {
        mainContent = freeRoomContent;
    }

    const baseInner = (
        <>
            {mainContent}
            {showQuestions && <div className='questionIndicator'></div>}
            {showProxy && <div className='proxyIndicator'></div>}
            {showBulk && <div className='bulkIndicator'></div>}
        </>
    );

    return (
        <td className='bookingsTableSingleRoomData' key={props.roomId}>
            {applyTooltips(baseInner)}
        </td>
    );
};





// props:
// lessonStructure - used for the row labels
// roomsData - the details of each lesson and the booking for it
// onFreeRoomClick - handler when a free lesson is selected

export function DailyBookingsTable(props) {

    const lessonStructure = props.lessonStructure || [];
    const rooms = props.roomsData || {};
    const date = props.date;

    // Get the names of the rooms from the keys in the "rooms" object
    const roomIds = Object.keys(rooms);
    // Allocate column widths: first column for lesson labels, remainder split equally
    const firstColumnWidth = '15%';
    const otherColumnWidth = roomIds.length > 0 ? `${(85 / roomIds.length).toFixed(2)}%` : 'auto';

    if (roomIds.length === 0) {
        return (
            <div className="bookingsErrorDiv">
                <img className='sadSeBookings' src={sadSE} alt='Sad School Engine' />
                <h1 className='bookingsErrorHeaderText'>No booking information for the selected date</h1>
                <h3 className='bookingsErrorMessage'>Please choose a different date</h3>
            </div>
        );
    }

    return (
        <table className='bookingsTable'>
            <colgroup>
                <col style={{ width: firstColumnWidth }} />
                {roomIds.map((roomId) => (
                    <col key={`col-${roomId}`} style={{ width: otherColumnWidth }} />
                ))}
            </colgroup>
            <thead>
                <tr className='bookingsTableHeader'>
                    <th>Lesson</th>
                    {roomIds.map((roomId) => (
                        <th key={roomId}>
                            {rooms[roomId].name}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {lessonStructure.map((lesson) => (
                    <tr className='bookingsTableRow' key={lesson.name}>
                        <td className='bookingsTableData'>{lesson.name}</td>
                        {roomIds.map((roomId) => (
                                <DailyBookingsTableCell
                                key={roomId}
                                cancelBookingFunction={props.cancelBookingFunction}
                                roomName={rooms[roomId].name}
                                rooms={rooms}
                                roomId={roomId}
                                lesson={lesson}
                                onFreeRoomClick={props.onFreeRoomClick}
                                onEditClick={props.onEditClick}
                                onBulkCancelClick={props.onBulkCancelClick}
                                date={date}
                                refreshingSlot={props.refreshingSlot}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );

};


// props:
// lessonStructure - array of lessons for row labels
// roomData - booking details for a single room
// weekDates - array of objects {api, name, display} for each day in the week
// onFreeRoomClick - handler when a free lesson is selected

export function WeeklyBookingsTableCell(props) {
    const { roomData, lesson, dateInfo, cancelBookingFunction, onFreeRoomClick, onEditClick, onBulkCancelClick } = props;
    const isRefreshingSlot = Boolean(
        props.refreshingSlot &&
        props.refreshingSlot.roomId === roomData.id &&
        props.refreshingSlot.lessonId === lesson.name &&
        props.refreshingSlot.date === dateInfo.api
    );

    if (isRefreshingSlot) {
        const visualClass = props.refreshingSlot?.previousVisual === 'occupied'
            ? 'bookingsSlotRefreshing--occupied'
            : 'bookingsSlotRefreshing--free';
        return (
            <td className='bookingsTableSingleRoomData'>
                <div className={`bookingsSlotRefreshing ${visualClass}`}>
                    <CgSpinner className='bookingsSlotRefreshingSpinner' />
                </div>
            </td>
        );
    }

    const dayBookings = roomData.bookingData[dateInfo.api] || {};
    const bookingInfo = dayBookings[lesson.name] || {
        userId: '',
        class: '',
        ownBooking: false,
        questions: [],
        bookedBy: '',
        origin: '',
        userCogId: '',
        bookedByCogId: ''
    };
    const questionEntries = Array.isArray(bookingInfo.questions) ? bookingInfo.questions : [];
    const hasQuestions = questionEntries.length > 0;
    const proxyBooked = bookingInfo.origin === 'PROXY';
    const isBulk = Boolean(bookingInfo.seriesId);
    const ownBooking = bookingInfo.ownBooking === true;
    const cancellable = bookingInfo.origin !== 'TIMETABLED';

    const roomPerm = roomData.perm || 'view';
    const isPastDate = moment(dateInfo.api, 'DDMMyyyy').isBefore(moment(), 'day');
    const readOnlyPast = isPastDate && roomPerm !== 'manage';
    const viewOnly = roomPerm === 'view';
    const showQuestions = hasQuestions && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const showProxy = proxyBooked && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const showBulk = isBulk && (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));
    const canEditQuestions = !readOnlyPast && !viewOnly && hasQuestions &&
        (roomPerm === 'manage' || (roomPerm === 'edit' && ownBooking));

    const handleEditClick = () => onEditClick(
        roomData.id,
        lesson.name,
        roomData.name,
        bookingInfo,
        dateInfo.api,
        dateInfo.name,
        dateInfo.display
    );

    // Build grouped tooltip content
    const tooltipGroups = [];
    if (showQuestions) {
        tooltipGroups.push({ color: '#c48e38', items: questionEntries });
    }
    if (showProxy) {
        tooltipGroups.push({ color: '#54abcd', items: [`Booked by ${bookingInfo.bookedBy}`] });
    }
    if (showBulk) {
        tooltipGroups.push({ color: '#ff99ff', items: ['Bulk booking'] });
    }

    const applyTooltips = (content) => (
        tooltipGroups.length > 0 ? (
            <TooltipList groups={tooltipGroups}>{content}</TooltipList>
        ) : (
            content
        )
    );

    const bookedContent = (
        <React.Fragment>
            {bookingInfo.userId}
            <br />
            {bookingInfo.class}
        </React.Fragment>
    );

    const ownBookingContent = (
        <UnavailableRoom
            user={bookingInfo.userId}
            class={bookingInfo.class}
            lesson={lesson.name}
            room={roomData.id}
            roomName={roomData.name}
            cancelBookingFunction={(type, room, lessonId, questions, roomName) =>
                cancelBookingFunction(type, room, lessonId, questions, roomName, dateInfo.api, dateInfo.name, dateInfo.display)
            }
            onEditClick={canEditQuestions ? handleEditClick : undefined}
            onCancelClick={showBulk ? () => onBulkCancelClick(
                roomData.id,
                lesson.name,
                roomData.name,
                bookingInfo,
                dateInfo.api,
                dateInfo.name,
                dateInfo.display
            ) : undefined}
        />
    );

    const freeRoomContent = (
        <FreeRoomButton
            lesson={lesson.name}
            room={roomData.id}
            roomName={roomData.name}
            onClick={(room, lessonId, roomName) =>
                onFreeRoomClick(room, lessonId, roomName, dateInfo.api, dateInfo.name, dateInfo.display)
            }
        />
    );

    if (readOnlyPast) {
        const readOnlyContent = (
            <>
                {bookingInfo.userId ? bookedContent : <div>Free</div>}
                {showQuestions && <div className='questionIndicator'></div>}
                {showProxy && <div className='proxyIndicator'></div>}
                {showBulk && <div className='bulkIndicator'></div>}
            </>
        );

        return (
            <td className='bookingsTableSingleRoomData'>
                {applyTooltips(<div className='pastBookingCell'>{readOnlyContent}</div>)}
            </td>
        );
    }

    if (viewOnly) {
        const viewOnlyContent = bookingInfo.userId !== '' ? bookedContent : <div className='freeRoomButton'>Available</div>;
        return (
            <td className='bookingsTableSingleRoomData'>
                {applyTooltips(viewOnlyContent)}
            </td>
        );
    }

    let mainContent;
    if (bookingInfo.userId !== '') {
        if (ownBooking && cancellable && (roomPerm === 'edit' || roomPerm === 'manage')) {
            mainContent = ownBookingContent;
        } else if (!ownBooking && cancellable && roomPerm === 'manage') {
            mainContent = (
                <UnavailableRoom
                    user={bookingInfo.userId}
                    class={bookingInfo.class}
                    lesson={lesson.name}
                    room={roomData.id}
                    roomName={roomData.name}
                    cancelBookingFunction={(type, room, lessonId, questions, roomName) =>
                        cancelBookingFunction(type, room, lessonId, questions, roomName, dateInfo.api, dateInfo.name, dateInfo.display)
                    }
                    onEditClick={canEditQuestions ? handleEditClick : undefined}
                    onCancelClick={showBulk ? () => onBulkCancelClick(
                        roomData.id,
                        lesson.name,
                        roomData.name,
                        bookingInfo,
                        dateInfo.api,
                        dateInfo.name,
                        dateInfo.display
                    ) : undefined}
                />
            );
        } else {
            mainContent = bookedContent;
        }
    } else {
        mainContent = freeRoomContent;
    }

    const baseContent = (
        <>
            {mainContent}
            {showQuestions && <div className='questionIndicator'></div>}
            {showProxy && <div className='proxyIndicator'></div>}
            {showBulk && <div className='bulkIndicator'></div>}
        </>
    );

    return (
        <td className='bookingsTableSingleRoomData'>
            {applyTooltips(baseContent)}
        </td>
    );
};

export function WeeklyBookingsTable(props) {
    const { lessonStructure, roomData, weekDates } = props;
    const firstColumnWidth = '15%';
    const otherColumnWidth = `${(85 / weekDates.length).toFixed(2)}%`;

    return (
        <table className='bookingsTable'>
            <colgroup>
                <col style={{ width: firstColumnWidth }} />
                {weekDates.map((d) => (
                    <col key={`col-${d.api}`} style={{ width: otherColumnWidth }} />
                ))}
            </colgroup>
            <thead>
                <tr className='bookingsTableHeader'>
                    <th>Lesson</th>
                    {weekDates.map((d) => (
                        <th key={d.api}>{d.name}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {lessonStructure.map((lesson) => (
                    <tr className='bookingsTableRow' key={lesson.name}>
                        <td className='bookingsTableData'>{lesson.name}</td>
                        {weekDates.map((d) => (
                            <WeeklyBookingsTableCell
                                key={d.api}
                                roomData={roomData}
                                lesson={lesson}
                                dateInfo={d}
                                cancelBookingFunction={props.cancelBookingFunction}
                                onFreeRoomClick={props.onFreeRoomClick}
                                onEditClick={props.onEditClick}
                                onBulkCancelClick={props.onBulkCancelClick}
                                refreshingSlot={props.refreshingSlot}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
